import React, { useState, useEffect } from 'react';
import PromptDialog from './PromptDialog';
import logger from '../utils/logger';

let promptListener = null;
let promptResolvers = {};

export const PromptContainer = () => {
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    promptListener = (prompt) => {
      setPrompts(prev => [...prev, prompt]);
    };

    return () => {
      promptListener = null;
      promptResolvers = {};
    };
  }, []);

  const handleClose = (id, value) => {
    // Résoudre la promesse
    if (promptResolvers[id]) {
      promptResolvers[id](value);
      delete promptResolvers[id];
    }

    // Retirer le prompt
    setPrompts(prev => prev.filter(p => p.id !== id));
  };

  return (
    <>
      {prompts.map(prompt => (
        <PromptDialog
          key={prompt.id}
          prompt={prompt}
          onClose={handleClose}
        />
      ))}
    </>
  );
};

// Service de prompts - retourne une promesse
let promptId = 0;

export const promptDialog = (options) => {
  return new Promise((resolve) => {
    if (!promptListener) {
      logger.error('PromptContainer not mounted');
      resolve(null);
      return;
    }

    const id = promptId++;
    const prompt = {
      id,
      title: options.title,
      message: options.message,
      placeholder: options.placeholder,
      defaultValue: options.defaultValue || '',
      required: options.required !== false, // Par défaut true
      confirmText: options.confirmText || 'Confirmer',
      type: options.type || 'input', // 'input' ou 'textarea'
      inputType: options.inputType || 'text', // 'text', 'password', 'email', etc.
    };

    promptResolvers[id] = resolve;
    promptListener(prompt);
  });
};

// Helper functions pour des cas d'usage courants
export const prompt = {
  // Prompt simple
  ask: (message, defaultValue = '', title = null) => {
    return promptDialog({
      title: title || 'Saisir une valeur',
      message,
      defaultValue,
      required: false
    });
  },

  // Prompt requis
  askRequired: (message, placeholder = '', title = null) => {
    return promptDialog({
      title: title || 'Saisir une valeur',
      message,
      placeholder,
      required: true
    });
  },

  // Prompt avec titre et message
  askWithTitle: (title, message, options = {}) => {
    return promptDialog({
      title,
      message,
      ...options
    });
  },

  // Textarea
  askLong: (message, defaultValue = '', title = null) => {
    return promptDialog({
      title: title || 'Saisir un commentaire',
      message,
      defaultValue,
      type: 'textarea',
      required: false
    });
  },

  // Textarea requis
  askLongRequired: (message, placeholder = '', title = null) => {
    return promptDialog({
      title: title || 'Saisir un commentaire',
      message,
      placeholder,
      type: 'textarea',
      required: true
    });
  },

  // Password
  askPassword: (message, placeholder = '', title = null) => {
    return promptDialog({
      title: title || 'Mot de passe',
      message,
      placeholder,
      inputType: 'password',
      required: true
    });
  },

  // Commentaire (alias pour askLong avec titre personnalisé)
  askComment: (message, defaultValue = '', required = false) => {
    return promptDialog({
      title: 'Commentaire',
      message,
      defaultValue,
      type: 'textarea',
      required
    });
  },

  // Motif de refus
  askRefusalReason: (message = 'Veuillez indiquer le motif du refus', required = true) => {
    return promptDialog({
      title: 'Motif du refus',
      message,
      placeholder: 'Saisir le motif...',
      type: 'textarea',
      required
    });
  }
};

export default PromptContainer;
